import React from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

/**
 * Public About page — section layout inspired by professional trading sites
 * (e.g. https://unitedkings.net/about-us/) with AlphaSniper-specific copy:
 * copy trading, execution automation, and risk transparency — no third-party claims.
 */
const HIGHLIGHT_STATS = [
  {
    label: "Execution model",
    value: "Auto copy",
    detail: "Trades mirrored to your account with minimal manual steps.",
  },
  {
    label: "Risk posture",
    value: "Risk-first",
    detail: "Sizing, stops, and copy controls you can adjust over time.",
  },
  {
    label: "Reporting",
    value: "Transparent",
    detail: "Performance and methodology pages built for serious review.",
  },
  {
    label: "Onboarding",
    value: "Guided",
    detail: "Clear path from signup to a connected, active slot.",
  },
];

const WHY_CHOOSE = [
  {
    title: "Risk management first",
    text: "Copy trading still carries market risk; we emphasize controls, discipline, and realistic expectations—not hype.",
  },
  {
    title: "Automation where it matters",
    text: "Reduce execution delay and manual errors by automating copy to your broker account when configured.",
  },
  {
    title: "Professional process",
    text: "Strategy work stays structured; you focus on account setup, risk settings, and long-term consistency.",
  },
  {
    title: "Dedicated client flows",
    text: "Subscription, slots, and trading accounts are organized so you always know what to do next.",
  },
  {
    title: "Results you can review",
    text: "Explore published performance context on our Results page—past performance is not a guarantee of future results.",
  },
  {
    title: "Time-efficient",
    text: "Less time chasing discretionary calls; more time on risk, review, and portfolio decisions.",
  },
];

const CORE_VALUES = [
  {
    title: "Precision",
    text: "Clear rules, clear reporting, and a product built for traders who care about process.",
  },
  {
    title: "Community & clarity",
    text: "We grow through honest communication and education—not unrealistic promises.",
  },
  {
    title: "Transparency",
    text: "Legal pages, refund rules, and methodology are published for informed decisions.",
  },
  {
    title: "Consistency",
    text: "We optimize for repeatable execution and sustainable risk—not one-off lottery trades.",
  },
];

export default function AboutUs() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-300">
            About AlphaSniper Trading
          </p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl md:text-[2.25rem] md:leading-tight">
            Execution-grade copy trading for traders who outgrew noisy signal feeds
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            We help serious retail traders connect a disciplined algo-style workflow to their own
            brokerage account—so strategy delivery, timing, and risk controls stay aligned without
            living in chat apps all day.
          </p>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            We are always developing: researching ideas, refining strategies, and upgrading our
            copy-trading workflow. We never hand clients untested or dubious strategies—only what we
            have validated—so you receive our best work, not experiments.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/SignUp"
              className="inline-flex rounded-xl bg-brand-500 px-8 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600"
            >
              Create account
            </Link>
            <Link
              to="/HowItWorks"
              className="inline-flex rounded-xl border border-white/25 bg-white/5 px-8 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              How it works
            </Link>
          </div>
        </header>

        <section className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHT_STATS.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center sm:text-left"
            >
              <p className="text-2xl font-extrabold text-white">{item.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-300">
                {item.label}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">{item.detail}</p>
            </div>
          ))}
        </section>

        <section className="mt-16">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">Our story</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-400">
            From a focus on execution quality to a product built around copy infrastructure
          </p>
          <div className="mt-10 space-y-8">
            <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
              <h3 className="text-lg font-bold text-brand-300">The beginning</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-300 sm:text-base">
                AlphaSniper was shaped by a simple frustration: great trade ideas often die in the
                gap between &ldquo;signal sent&rdquo; and &ldquo;order filled.&rdquo; We set out to
                build a path where compatible accounts can follow a structured process with
                automated copy—without surrendering ownership of funds or broker access.
              </p>
            </article>
            <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
              <h3 className="text-lg font-bold text-brand-300">Our growth</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-300 sm:text-base">
                The platform matured around real operational constraints: broker compatibility,
                slot-based access, risk defaults for early onboarding, and reporting clients can
                actually audit. Every iteration prioritizes stable execution and clear client
                responsibilities—because copy trading only works when both sides understand the
                rules.
              </p>
            </article>
            <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
              <h3 className="text-lg font-bold text-brand-300">Our commitment</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-300 sm:text-base">
                We do not promise effortless wealth. We commit to transparent pricing, published
                policies, and a roadmap aligned with risk-aware trading—helping you decide if our
                model fits your capital, horizon, and tolerance for drawdowns.
              </p>
            </article>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">Why traders choose us</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-400">
            Professional workflow meets practical automation—without turning trading into a black
            box you cannot question.
          </p>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2">
            {WHY_CHOOSE.map((item) => (
              <li
                key={item.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-sm text-gray-200"
              >
                <h3 className="font-semibold text-white">{item.title}</h3>
                <p className="mt-2 leading-relaxed text-gray-400">{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-16">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">Our core values</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-gray-400">
            The principles behind product decisions and client communications
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {CORE_VALUES.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <h3 className="text-lg font-semibold text-brand-300">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-2xl border border-brand-500/30 bg-brand-500/10 p-8 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-200">
            Ready to explore
          </p>
          <h2 className="mt-2 text-xl font-bold sm:text-2xl">
            See pricing, performance context, and onboarding in one place
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-gray-300">
            Start with our public pages, then create an account when you are ready to connect a
            slot and broker credentials.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/Pricing"
              className="inline-flex rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white hover:bg-brand-600"
            >
              View pricing
            </Link>
            <Link
              to="/Results"
              className="text-sm font-semibold text-brand-200 underline-offset-2 hover:underline"
            >
              View results
            </Link>
          </div>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
